// ─── FOLLOW THE MONEY — congressional network intelligence + D3 force graph ───
import React, { useState, useEffect, useRef, useCallback } from "react";
import * as d3 from "d3";
import { C, font, backendFetch, AnalystButton } from "./core";

async function fetchJSON(path) {
  const res = await backendFetch(path, path);
  if (!res.ok) throw new Error("HTTP " + res.status);
  return res.json();
}

// Node color by entity type; edge color by relationship type.
const NODE_COLORS = {
  ticker: C.amber, politician: C.purple, corporation: "#FFB800",
  person: "#E0E0E0", pac: C.red, committee: "#4FC3F7", fund: "#81C784",
};
const EDGE_COLORS = {
  traded: C.amber, holds: C.amber, etf_holds: C.amber,
  donated: C.red, employed: C.textDim, board_member: C.textDim,
  membership: C.textDim, lobbying: C.textDim, ownership: C.textDim,
  contracts: "#4FC3F7", committee: "#4FC3F7",
  insider_buy: C.purple, insider_sell: C.purple,
};
const nodeColor = (t) => NODE_COLORS[t] || C.textMid;
const edgeColor = (t) => EDGE_COLORS[t] || C.textDim;
const nodeRadius = (d) => 7 + (d.weight || 0.3) * 18;

// Undulating background for the cold (no-network) state.
function ColdBackground() {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return undefined;
    const ctx = cv.getContext("2d");
    let raf, t = 0, running = true;
    const resize = () => { cv.width = cv.offsetWidth; cv.height = cv.offsetHeight; };
    resize();
    const draw = () => {
      if (!running) return;
      const W = cv.width, H = cv.height;
      ctx.clearRect(0, 0, W, H);
      for (let i = 0; i < 6; i++) {
        ctx.beginPath();
        ctx.strokeStyle = `rgba(0,255,135,${0.05 + i * 0.008})`;
        ctx.lineWidth = 1;
        for (let x = 0; x <= W; x += 8) {
          const y = H / 2 + Math.sin(x * 0.006 + t * 0.02 + i * 0.7) * (40 + i * 16) + (i - 2.5) * 34;
          if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      t += 1;
      raf = requestAnimationFrame(draw);
    };
    draw();
    window.addEventListener("resize", resize);
    return () => { running = false; cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={ref} style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />;
}

function buildContext(g) {
  if (!g) return "";
  const anchor = g.metadata?.anchor;
  const nodeList = (g.nodes || []).map((n) => `${n.type}:${n.label}`).slice(0, 50).join("; ");
  const edgeList = (g.edges || []).map((e) => e.label || e.type).slice(0, 40).join("; ");
  return (
    `Follow-the-Money network for ${anchor} (${g.metadata?.anchor_type}). ` +
    `Nodes (${(g.nodes || []).length}): ${nodeList}. Edges (${(g.edges || []).length}): ${edgeList}. ` +
    `Analyze this network. What does the convergence of these signals suggest about ${anchor}? ` +
    `What relationships are most significant?`
  );
}

// ── Presentation helpers — relationship data is platform intelligence: human
//    labels only, never raw category ids / entity ids / external source names ──
const CATEGORY_LABELS = {
  1: "Position / Employment", 3: "Membership / Board",
  5: "Campaign Finance Donation", 6: "Financial Transaction",
  7: "Lobbying Relationship", 10: "Ownership",
};
const FUND_LABEL = { NANC: "Democrat-aligned ETF", KRUZ: "Republican-aligned ETF" };
// Relationship verbs used to split a "<name> gave money to <other>" description.
const REL_VERB = /\s+(?:gave money to|donated to|is an?|was an?|owns|owned|lobbied|works for|member of|on the board of)\s+/i;

const _clean = (s) => String(s || "").replace(/\s+/g, " ").trim();
const fmtMoney = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return null;
  const a = Math.abs(n);
  if (a >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};
// graph.edges keeps STRING source/target (D3 mutates a mapped copy, not this array).
const edgesTouching = (graph, id) => (graph?.edges || []).filter((e) => e.source === id || e.target === id);
const entityName = (node, graph) => {
  const edge = edgesTouching(graph, node.id)[0];
  const desc = _clean(edge?.label) || _clean(node.label);
  return _clean(desc.split(REL_VERB)[0]) || _clean(node.label);
};
// Format an articles array (fetched ticker news, or a globalSignals array) into
// headline strings. Prefers the human age label; `source` is a plain string on
// our feed. Empty when there are no articles → the news section stays hidden.
const formatHeadlines = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .map((a) => _clean(`${a?.title || a?.headline || ""}${a?.source ? " — " + (a.source.name || a.source) : ""}${a?.ageLabel || a?.publishedAt || a?.date ? " · " + (a.ageLabel || a.publishedAt || a.date) : ""}`))
    .filter(Boolean);

const DTitle = ({ color, children }) => (
  <div style={{ fontSize: 10, letterSpacing: "0.1em", color: color || C.textDim, fontWeight: 700 }}>{children}</div>
);
const DName = ({ children }) => (
  <div style={{ fontSize: 15, fontWeight: 700, color: C.text, margin: "4px 0 10px" }}>{children}</div>
);
const DRow = ({ label, color, children }) => (
  <div style={{ fontSize: 12, color: color || C.textMid, marginBottom: 4, lineHeight: 1.45 }}>
    {label ? <span style={{ color: C.textDim }}>{label} </span> : null}{children}
  </div>
);

// Human-readable node detail — no raw ids, no external source attribution.
// How many committee members show before the list needs expanding.
const MEMBERS_COLLAPSED = 8;

function NodeDetail({ node, graph }) {
  // ⚠ This hook MUST sit above every `if (node.type === …)` early return below —
  // hook order has to be identical on every render, and those returns differ by
  // node type. Reset comes from key={node.id} at the render site, which remounts
  // this component whenever the selection changes.
  const [membersOpen, setMembersOpen] = useState(false);

  const d = node.data || {};
  const byId = {};
  (graph?.nodes || []).forEach((n) => { byId[n.id] = n; });
  const touching = edgesTouching(graph, node.id);
  // ⚠⚠ Deduped by node id. `touching` is a list of EDGES, so a pair joined by more
  // than one edge yielded the same node once per edge — a member with 13 trades
  // rendered 13 times in a committee's Members list. This helper returns
  // NEIGHBOURS, so one entry per node is its contract no matter what the graph
  // contains; the backend stopped emitting those duplicates too, but this stays
  // correct on its own terms and covers all six call sites at once.
  // ⚠ Edge COUNTS elsewhere still count edges on purpose — 13 trades genuinely are
  // 13 trades, so `trades.length` below must NOT be deduped.
  const neighbours = (pred) => {
    const seen = new Set();
    const out = [];
    for (const e of touching) {
      const n = byId[e.source === node.id ? e.target : e.source];
      if (!n || seen.has(n.id) || (pred && !pred(n))) continue;
      seen.add(n.id);
      out.push(n);
    }
    return out;
  };

  if (node.type === "politician") {
    const fin = d.finance || {};
    const committees = neighbours((n) => n.type === "committee");
    const trades = touching.filter((e) => e.type === "traded");
    const sectors = Object.entries(fin.sector_totals || {}).sort((a, b) => b[1] - a[1]);
    return (
      <>
        <DTitle color={nodeColor("politician")}>MEMBER OF CONGRESS</DTitle>
        <DName>{node.label}</DName>
        <DRow>{[d.party, d.chamber, d.state].filter((x) => x && x !== "—").join(" · ") || "—"}</DRow>
        {committees.length ? <DRow label="Committees:">{committees.map((c) => c.label).join("; ")}</DRow> : null}
        {trades.length ? <DRow label="Recent trades:">{trades.length} in last 90d</DRow> : null}
        {fmtMoney(fin.receipts) ? <DRow label="Campaign receipts:">{fmtMoney(fin.receipts)} this cycle</DRow> : null}
        {fmtMoney(fin.pac_contributions) ? <DRow label="PAC money:">{fmtMoney(fin.pac_contributions)}</DRow> : null}
        {sectors.length ? <DRow label="Top donor sectors:">{sectors.slice(0, 3).map(([s, v]) => `${s} (${fmtMoney(v)})`).join(", ")}</DRow> : null}
      </>
    );
  }

  if (node.type === "ticker") {
    const trades = touching.filter((e) => e.type === "traded");
    const funds = neighbours((n) => n.type === "fund");
    const agencies = neighbours((n) => n.type === "corporation" && n.data?.total != null && n.data?.category_id == null && n.data?.net_bias == null && n.data?.source !== "fec");
    const insiders = touching.filter((e) => e.type === "insider_buy" || e.type === "insider_sell");
    const dark = neighbours((n) => n.type === "corporation" && n.data?.net_bias != null);
    return (
      <>
        <DTitle color={nodeColor("ticker")}>TICKER</DTitle>
        <DName>{node.label}</DName>
        {trades.length ? <DRow label="Congressional trades:">{trades.length}</DRow> : null}
        {agencies.map((a) => <DRow key={a.id} label="Federal contracts:">{fmtMoney(a.data?.total)} · {a.label}</DRow>)}
        {funds.map((f) => <DRow key={f.id} label="ETF positioning:">{f.label}{f.data?.weight != null ? ` ${Number(f.data.weight).toFixed(2)}%` : ""} · {FUND_LABEL[f.label] || "congressional ETF"}</DRow>)}
        {insiders.length ? <DRow label="Insider activity:">{insiders.length} open-market {insiders.every((e) => e.type === "insider_buy") ? "buys" : insiders.every((e) => e.type === "insider_sell") ? "sells" : "trades"}</DRow> : null}
        {dark.map((x) => <DRow key={x.id} label="Dark pool:">score {x.data?.score} · {x.data?.net_bias}</DRow>)}
        <DRow color={C.textDim}>Full breakdown in SIGNALS DETECTED below.</DRow>
      </>
    );
  }

  if (node.type === "fund") {
    return (
      <>
        <DTitle color={nodeColor("fund")}>ETF</DTitle>
        <DName>{node.label}</DName>
        <DRow>{FUND_LABEL[node.label] || "Congressional-trading ETF"}</DRow>
        {d.weight != null ? <DRow label="Portfolio weight:">{Number(d.weight).toFixed(2)}%</DRow> : null}
      </>
    );
  }

  if (node.type === "pac") {
    const recipients = neighbours((n) => n.type === "politician");
    return (
      <>
        <DTitle color={nodeColor("pac")}>PAC</DTitle>
        <DName>{node.label}</DName>
        <DRow>Political Action Committee</DRow>
        {fmtMoney(d.total) ? <DRow label="Total donated:">{fmtMoney(d.total)}</DRow> : null}
        {recipients.length ? <DRow label="Recipients:">{recipients.map((r) => r.label).join(", ")}</DRow> : null}
      </>
    );
  }

  if (node.type === "committee") {
    const members = neighbours((n) => n.type === "politician");
    // ⚠ Truncation here is REVERSIBLE, unlike the .slice(0, N) calls elsewhere in
    // this file, which drop the remainder with no way to reach it. The toggle only
    // renders when there is actually something hidden.
    const shown = membersOpen ? members : members.slice(0, MEMBERS_COLLAPSED);
    const hidden = members.length - shown.length;
    return (
      <>
        <DTitle color={nodeColor("committee")}>COMMITTEE</DTitle>
        <DName>{node.label}</DName>
        <DRow>Congressional Committee</DRow>
        {members.length ? (
          <DRow label="Members:">
            {shown.map((m) => m.label).join(", ")}
            {members.length > MEMBERS_COLLAPSED ? (
              <span
                onClick={() => setMembersOpen((p) => !p)}
                style={{ color: C.textDim, cursor: "pointer", marginLeft: 6, userSelect: "none" }}
              >
                {membersOpen ? "▴ less" : `▾ ${hidden} more`}
              </span>
            ) : null}
          </DRow>
        ) : null}
      </>
    );
  }

  // corporation / person sub-kinds
  const isRel = d.category_id != null;              // relationship entity
  const isDark = d.net_bias != null;                // dark pool
  const isDonor = d.source === "fec";               // FEC donor
  const isAgency = d.total != null && !isRel && !isDark && !isDonor;

  if (isDark) {
    return (
      <>
        <DTitle color={nodeColor("corporation")}>DARK POOL</DTitle>
        <DName>Dark Pool Activity</DName>
        {d.score != null ? <DRow label="Score:">{d.score}</DRow> : null}
        {d.net_bias ? <DRow label="Net bias:">{d.net_bias}</DRow> : null}
      </>
    );
  }
  if (isAgency) {
    return (
      <>
        <DTitle color={nodeColor("corporation")}>FEDERAL AGENCY</DTitle>
        <DName>{node.label}</DName>
        <DRow>Federal Contracting Agency</DRow>
        {fmtMoney(d.total) ? <DRow label="Awarded:">{fmtMoney(d.total)}</DRow> : null}
      </>
    );
  }
  if (isDonor) {
    const to = neighbours((n) => n.type === "politician")[0];
    return (
      <>
        <DTitle color={nodeColor("corporation")}>DONOR</DTitle>
        <DName>{node.label}</DName>
        <DRow>Campaign Finance Donor</DRow>
        {fmtMoney(d.total) ? <DRow label="Donated:">{fmtMoney(d.total)}{to ? ` to ${to.label}` : ""}</DRow> : null}
        {d.sector && d.sector !== "other" ? <DRow label="Sector:">{d.sector}</DRow> : null}
      </>
    );
  }
  // Relationship entity (formerly raw label + ids) — clean name + human relation.
  const name = isRel ? entityName(node, graph) : node.label;
  const rel = isRel ? (CATEGORY_LABELS[d.category_id] || "Connected Entity") : null;
  const linked = neighbours((n) => n.type === "politician")[0] || neighbours()[0];
  const linkVerb = d.category_id === 5 ? "Donated to" : "Connected to";
  return (
    <>
      <DTitle color={nodeColor(node.type)}>{node.type === "person" ? "PERSON" : "ENTITY"}</DTitle>
      <DName>{name}</DName>
      {node.type === "person" && d.title ? <DRow>{d.title}</DRow> : null}
      {rel ? <DRow>{rel}</DRow> : null}
      {linked && linked.label !== name ? <DRow color={C.textDim}>{linkVerb} {linked.label}</DRow> : null}
    </>
  );
}

// SIGNALS DETECTED — only the signals that fired (have data), derived from the graph.
function SignalsPanel({ graph, news, globalSignals, onNavigate }) {
  const nodes = graph?.nodes || [];
  const edges = graph?.edges || [];
  const byId = {};
  nodes.forEach((n) => { byId[n.id] = n; });
  const items = [];

  const trades = edges.filter((e) => e.type === "traded");
  if (trades.length) {
    items.push({ title: "CONGRESSIONAL TRADE", lines: trades.slice(0, 4).map((e) => {
      const a = byId[e.source], b = byId[e.target];
      const pol = a?.type === "politician" ? a : b;
      const tk = a?.type === "ticker" ? a : b;
      const dd = e.data || {};
      return _clean(`${pol?.label || "Member"} ${dd.type || ""} ${tk?.label || ""}${dd.date ? " · " + dd.date : ""}${dd.size ? " · " + dd.size : ""}`);
    }) });
  }
  const committees = nodes.filter((n) => n.type === "committee");
  if (committees.length) items.push({ title: "COMMITTEE RELEVANCE", lines: committees.slice(0, 4).map((n) => n.label) });

  const agencies = nodes.filter((n) => n.type === "corporation" && n.data?.total != null && n.data?.category_id == null && n.data?.net_bias == null && n.data?.source !== "fec");
  if (agencies.length) items.push({ title: "FEDERAL CONTRACTS", lines: agencies.map((n) => `${fmtMoney(n.data?.total)} awarded by ${n.label}`) });

  const funds = nodes.filter((n) => n.type === "fund");
  if (funds.length) items.push({ title: "ETF POSITIONING", lines: funds.map((n) => `${n.label}: ${n.data?.weight != null ? Number(n.data.weight).toFixed(2) + "%" : "held"} · ${FUND_LABEL[n.label] || ""}`.trim()) });

  const ins = edges.filter((e) => e.type === "insider_buy" || e.type === "insider_sell");
  if (ins.length) {
    const buys = ins.filter((e) => e.type === "insider_buy").length;
    items.push({ title: "INSIDER ACTIVITY", lines: [`${buys} open-market buys · ${ins.length - buys} sells`] });
  }
  const dark = nodes.filter((n) => n.type === "corporation" && n.data?.net_bias != null);
  if (dark.length) items.push({ title: "DARK POOL", lines: dark.map((n) => `Score ${n.data?.score} · ${n.data?.net_bias}`) });

  const donors = nodes.filter((n) => (n.type === "corporation" && n.data?.source === "fec") || n.type === "pac");
  if (donors.length) {
    const sorted = donors.slice().sort((a, b) => (b.data?.total || 0) - (a.data?.total || 0));
    items.push({ title: "CAMPAIGN DONORS", lines: sorted.slice(0, 4).map((n) => `${n.label}${fmtMoney(n.data?.total) ? " · " + fmtMoney(n.data.total) : ""}`) });
  }
  const headlines = (Array.isArray(news) && news.length)
    ? formatHeadlines(news)
    : formatHeadlines(globalSignals?.newsSentiment?.articles || globalSignals?.headlines || globalSignals?.news);
  if (headlines.length) items.push({ title: "RECENT HEADLINES", icon: "📰", lines: headlines.slice(0, 3), news: true });

  if (!items.length) return null;
  return (
    <div style={{ marginTop: 18, borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
      <div style={{ fontSize: 11, color: C.textDim, marginBottom: 8, letterSpacing: "0.08em" }}>SIGNALS DETECTED</div>
      {items.map((it) => (
        <div key={it.title} style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: C.green, fontWeight: 700 }}>{it.icon || "✅"} {it.title}</div>
          {it.lines.filter(Boolean).map((ln, i) => (
            <div key={i} style={{ fontSize: 11, color: C.textDim, marginLeft: 16, marginTop: 2, lineHeight: 1.4 }}>{ln}</div>
          ))}
          {it.news && onNavigate ? (
            <div onClick={() => onNavigate("NEWS")}
              style={{ fontSize: 10, color: C.amber, cursor: "pointer", marginLeft: 16, marginTop: 3 }}>
              → More in News tab
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

const MODES = [
  { key: "ticker", label: "TICKER" },
  { key: "member", label: "MEMBER" },
  { key: "signal", label: "SIGNAL" },
];

export default function FollowTheMoneyTab({ keys, globalSignals, onNavigate, ftmFocus }) {
  const [mode, setMode] = useState("ticker");
  const [query, setQuery] = useState("");
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [suggestions, setSuggestions] = useState(null);
  const [news, setNews] = useState([]);
  const svgRef = useRef(null);
  const wrapRef = useRef(null);
  const simRef = useRef(null);

  const loadNetwork = useCallback(async (anchor, type) => {
    if (!anchor) return;
    setLoading(true); setError(""); setSelected(null); setSuggestions(null);
    try {
      const g = await fetchJSON(`/api/ftm/network?anchor=${encodeURIComponent(anchor)}&type=${type}`);
      setGraph(g && Array.isArray(g.nodes) ? g : null);
      if (!g || !g.nodes?.length) setError("No network found for this anchor.");
    } catch {
      setError("Failed to build network."); setGraph(null);
    }
    setLoading(false);
  }, []);

  const runSearch = () => { const q = query.trim(); if (q) loadNetwork(q, mode); };

  // ── External deep link (Congressional → Follow the Money) ──
  // App passes {name, nonce}; the nonce re-fires this even when the same member is
  // clicked twice. Seeds the search field + member mode and builds the network
  // immediately, so the user lands here with the graph already loading. The
  // suggestion fetch is skipped for this seeded value (the anchor is already
  // known — no dropdown over the graph, no extra request).
  const skipSuggestRef = useRef(false);
  const focusName  = ftmFocus?.name;
  const focusNonce = ftmFocus?.nonce;
  useEffect(() => {
    if (!focusName) return;
    skipSuggestRef.current = true;
    setMode("member");
    setQuery(focusName);
    loadNetwork(focusName, "member");
  }, [focusName, focusNonce, loadNetwork]);

  // Ticker/signal anchors: pull recent headlines for the anchor ticker from the
  // News endpoint (member anchors have no ticker → skip). Graceful: [] on any
  // error, and empty when news sources are down, so the section just stays hidden.
  useEffect(() => {
    const anchor = graph?.metadata?.anchor;
    const atype = graph?.metadata?.anchor_type;
    if (!anchor || (atype !== "ticker" && atype !== "signal")) { setNews([]); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchJSON(`/api/news/ticker/${encodeURIComponent(anchor)}`);
        const arr = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        if (!cancelled) setNews(arr);
      } catch { if (!cancelled) setNews([]); }
    })();
    return () => { cancelled = true; };
  }, [graph]);

  // Debounced search suggestions.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setSuggestions(null); return undefined; }
    // Seeded by an external deep link — the anchor is already resolved.
    if (skipSuggestRef.current) { skipSuggestRef.current = false; setSuggestions(null); return undefined; }
    const id = setTimeout(async () => {
      try { setSuggestions(await fetchJSON(`/api/ftm/search?q=${encodeURIComponent(q)}`)); } catch { /* ignore */ }
    }, 350);
    return () => clearTimeout(id);
  }, [query]);

  // D3 force simulation + SVG render.
  useEffect(() => {
    if (!graph || !svgRef.current || !graph.nodes?.length) return undefined;
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    const W = wrapRef.current?.clientWidth || 900;
    const H = wrapRef.current?.clientHeight || 600;
    svg.attr("viewBox", `0 0 ${W} ${H}`);
    const g = svg.append("g");
    svg.call(d3.zoom().scaleExtent([0.2, 4]).on("zoom", (ev) => g.attr("transform", ev.transform)));
    svg.on("click", () => setSelected(null));

    const nodes = graph.nodes.map((n) => ({ ...n }));
    const links = graph.edges?.map((e) => ({ ...e })) || [];
    const sim = d3.forceSimulation(nodes)
      .force("link", d3.forceLink(links).id((d) => d.id)
        .distance((d) => 70 + (1 - (d.strength || 0.3)) * 70)
        .strength((d) => 0.2 + (d.strength || 0.3) * 0.5))
      .force("charge", d3.forceManyBody().strength(-260))
      .force("center", d3.forceCenter(W / 2, H / 2))
      .force("collide", d3.forceCollide().radius((d) => nodeRadius(d) + 6));
    simRef.current = sim;

    const link = g.append("g").selectAll("line").data(links).join("line")
      .attr("stroke", (d) => edgeColor(d.type)).attr("stroke-opacity", 0.5)
      .attr("stroke-width", (d) => 1 + (d.strength || 0.3) * 3);
    link.append("title").text((d) => d.label || d.type);

    const node = g.append("g").selectAll("g").data(nodes).join("g")
      .style("cursor", "pointer")
      .call(d3.drag()
        .on("start", (ev, d) => { if (!ev.active) sim.alphaTarget(0.3).restart(); d.fx = d.x; d.fy = d.y; })
        .on("drag", (ev, d) => { d.fx = ev.x; d.fy = ev.y; })
        .on("end", (ev, d) => { if (!ev.active) sim.alphaTarget(0); d.fx = null; d.fy = null; }))
      .on("click", (ev, d) => { ev.stopPropagation(); setSelected(d); setSidebarOpen(true); });

    node.append("circle")
      .attr("r", (d) => nodeRadius(d)).attr("fill", (d) => nodeColor(d.type))
      .attr("fill-opacity", 0.85).attr("stroke", C.bg).attr("stroke-width", 1.5);
    node.append("text")
      .text((d) => (d.label.length > 22 ? `${d.label.slice(0, 22)}…` : d.label))
      .attr("x", (d) => nodeRadius(d) + 4).attr("y", 4)
      .attr("fill", C.text).attr("font-size", 10).attr("font-family", font)
      .style("pointer-events", "none");

    sim.on("tick", () => {
      link.attr("x1", (d) => d.source.x).attr("y1", (d) => d.source.y)
        .attr("x2", (d) => d.target.x).attr("y2", (d) => d.target.y);
      node.attr("transform", (d) => `translate(${d.x},${d.y})`);
    });
    return () => { sim.stop(); };
  }, [graph]);

  useEffect(() => () => { if (simRef.current) simRef.current.stop(); }, []);

  const modeBtn = (m) => (
    <button
      key={m.key}
      onClick={() => setMode(m.key)}
      style={{
        padding: "6px 14px", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em",
        fontFamily: font, cursor: "pointer", borderRadius: 3,
        border: `1px solid ${mode === m.key ? C.amber : C.border}`,
        background: mode === m.key ? C.amberBg : "transparent",
        color: mode === m.key ? C.amber : C.textMid,
      }}
    >{m.label}</button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "84vh", fontFamily: font }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: "0.14em", color: C.amber }}>FOLLOW THE MONEY</div>
        <div style={{ fontSize: 11, color: C.textDim }}>congressional network intelligence</div>
      </div>

      {/* Search + mode */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap", position: "relative" }}>
        {MODES.map(modeBtn)}
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
          placeholder={mode === "member" ? "Enter member name…" : mode === "signal" ? "Enter signal ticker…" : "Enter ticker…"}
          style={{
            flex: 1, minWidth: 180, padding: "8px 12px", fontSize: 13, fontFamily: font,
            background: C.panelAlt, border: `1px solid ${C.border}`, borderRadius: 3, color: C.text,
          }}
        />
        <button onClick={runSearch} disabled={loading}
          style={{
            padding: "8px 18px", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", fontFamily: font,
            cursor: loading ? "default" : "pointer", borderRadius: 3, border: `1px solid ${C.amber}`,
            background: C.amberBg, color: C.amber, opacity: loading ? 0.6 : 1,
          }}>{loading ? "BUILDING…" : "BUILD NETWORK"}</button>

        {/* Suggestions dropdown */}
        {suggestions && (suggestions.tickers?.length || suggestions.members?.length || suggestions.entities?.length) ? (
          <div style={{
            position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4, zIndex: 20,
            background: C.panel, border: `1px solid ${C.border}`, borderRadius: 4, maxHeight: 260,
            overflowY: "auto", padding: 6,
          }}>
            {suggestions.tickers?.map((t) => (
              <div key={`t-${t}`} onClick={() => { setQuery(t); setMode("ticker"); loadNetwork(t, "ticker"); }}
                style={{ padding: "5px 8px", cursor: "pointer", color: C.amber, fontSize: 12 }}>
                <span style={{ color: C.textDim, marginRight: 6 }}>TICKER</span>{t}
              </div>
            ))}
            {suggestions.members?.map((m) => (
              <div key={`m-${m}`} onClick={() => { setQuery(m); setMode("member"); loadNetwork(m, "member"); }}
                style={{ padding: "5px 8px", cursor: "pointer", color: C.purple, fontSize: 12 }}>
                <span style={{ color: C.textDim, marginRight: 6 }}>MEMBER</span>{m}
              </div>
            ))}
            {suggestions.entities?.map((e) => (
              <div key={`e-${e.id}`} onClick={() => { setQuery(e.name); setMode("member"); loadNetwork(e.name, "member"); }}
                style={{ padding: "5px 8px", cursor: "pointer", color: C.textMid, fontSize: 12 }}>
                <span style={{ color: C.textDim, marginRight: 6 }}>{(e.primary_ext || "ENTITY").toUpperCase()}</span>{e.name}
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {/* Graph + sidebar */}
      <div style={{ display: "flex", flex: 1, minHeight: 0, gap: 8 }}>
        <div ref={wrapRef} style={{
          flex: 1, position: "relative", minWidth: 0, background: C.panelAlt,
          border: `1px solid ${C.border}`, borderRadius: 4, overflow: "hidden",
        }}>
          {(!graph || !graph.nodes?.length) && (
            <>
              <ColdBackground />
              <div style={{
                position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", textAlign: "center", padding: 20, pointerEvents: "none",
              }}>
                <div style={{ fontSize: 15, color: C.textMid, maxWidth: 420, lineHeight: 1.6 }}>
                  {error || "Enter a ticker, member name, or select a signal to build the network"}
                </div>
                <div style={{ fontSize: 11, color: C.textDim, marginTop: 10 }}>drag nodes · scroll to zoom · click a node for details</div>
              </div>
            </>
          )}
          {graph && graph.nodes?.length ? (
            <svg ref={svgRef} style={{ width: "100%", height: "100%", display: "block" }} />
          ) : null}
          {graph && graph.metadata ? (
            <div style={{ position: "absolute", top: 8, left: 10, fontSize: 11, color: C.textDim }}>
              {graph.nodes.length} nodes · {graph.edges.length} edges
            </div>
          ) : null}
          <button onClick={() => setSidebarOpen((o) => !o)}
            style={{
              position: "absolute", top: 8, right: 8, fontSize: 11, fontFamily: font, cursor: "pointer",
              padding: "4px 8px", borderRadius: 3, border: `1px solid ${C.border}`, background: C.panel, color: C.textMid,
            }}>{sidebarOpen ? "▶ hide" : "◀ details"}</button>
        </div>

        {/* Sidebar */}
        {sidebarOpen && (
          <div style={{
            width: 300, flexShrink: 0, background: C.panel, border: `1px solid ${C.border}`,
            borderRadius: 4, padding: 14, overflowY: "auto",
          }}>
            {selected ? (
              // ⚠ key remounts NodeDetail on every selection change. Without it the
              // same instance is reused, so the Members expand state would carry
              // over to the next committee — a 3-member one would render already
              // "expanded" with a stale collapse arrow.
              <NodeDetail key={selected.id} node={selected} graph={graph} />
            ) : (
              <div style={{ fontSize: 12, color: C.textDim, lineHeight: 1.6 }}>
                Click a node to inspect it. Nodes are congress members, insiders, ETFs, agencies, PACs, and
                connected entities around the anchor.
              </div>
            )}

            {graph && graph.nodes?.length ? <SignalsPanel graph={graph} news={news} globalSignals={globalSignals} onNavigate={onNavigate} /> : null}

            {graph && graph.nodes?.length ? (
              <div style={{ marginTop: 18, borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
                <div style={{ fontSize: 11, color: C.textDim, marginBottom: 8 }}>AI synthesis of this network</div>
                <AnalystButton
                  context={buildContext(graph)}
                  panel="signal_board"
                  label="⚡ SYNTHESIZE"
                  globalSignals={globalSignals}
                />
              </div>
            ) : null}

            {/* Legend */}
            <div style={{ marginTop: 18, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
              <div style={{ fontSize: 10, color: C.textDim, marginBottom: 6, letterSpacing: "0.08em" }}>LEGEND</div>
              {Object.entries(NODE_COLORS).map(([t, col]) => (
                <div key={t} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: col, display: "inline-block" }} />
                  <span style={{ fontSize: 11, color: C.textMid }}>{t}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
